import React, { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Select from 'react-select'
import styled, { css } from 'styled-components'

import Input from '../../common/components/Input'
import { firmwareInputValidation } from '../../common/constants/FormValidationSchemas'
import Text from '../Text'
import { ChargePointStatus } from '../../common/enums/ChargePointStatusEnum'

const STip = styled.div`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 1em;
`

export const SStatusIcon = styled.div<{ status: 'Online' | 'Charging' | 'Offline' }>`
    display: inline-block;
    vertical-align: middle;
    width: 12px;
    height: 12px;
    border-radius: 100%;
    margin-right: 0.5em;
    ${(p) =>
        p.status === 'Online'
            ? css`
                  background: ${({ theme }) => theme.brandColors.CA_Green};
              `
            : ''}
    ${(p) =>
        p.status === 'Charging'
            ? css`
                  background: ${({ theme }) => theme.brandColors.CA_Blue};
              `
            : ''}
    ${(p) =>
        p.status === 'Offline'
            ? css`
                  background: ${({ theme }) => theme.font.errorColor};
              `
            : ''}
`

const SButton = styled.button`
    margin-left: 1.5em;
`

interface PartnerOption {
    value: unknown
    label: string
}

const chargerStatuses = [
    { label: 'All chargers status', value: undefined },
    ...Object.values(ChargePointStatus).map((status) => ({
        label: (
            <React.Fragment>
                <SStatusIcon status={status} />
                {status}
            </React.Fragment>
        ),
        value: status,
    })),
]

type UpdateChargersFirmwareFiltersProps = {
    filters: FirmwareGetChargePointsByFirmwareTypeApiArg
    isFetching: boolean
    partnerOptions: Array<PartnerOption>
    productName: string
    pageClick: () => void
    onChange?: (fieldName: string, value: any) => void
    onSubmit: (filters: FirmwareGetChargePointsByFirmwareTypeApiArg, pageClick: () => void) => void
    onClearFilters: () => void
}

export type FirmwareFormData = {
    id?: string
    companyName?: string
    firmwareVersion?: string | number
    partnerId?: { value: number; label: string }
}

const UpdateChargersFirmwareFilters = ({
    partnerOptions,
    onClearFilters,
    filters,
    pageClick,
    isFetching,
    onChange,
    productName,
    onSubmit,
}: UpdateChargersFirmwareFiltersProps): React.ReactElement => {
    const {
        control,
        handleSubmit,
        reset,
        register,
        formState: { errors: formErrors, isValid },
    } = useForm<FirmwareFormData>({
        mode: 'onChange',
        delayError: 500,
        defaultValues: {
            id: filters.id ?? undefined,
            companyName: filters.companyName ?? undefined,
            firmwareVersion: filters.installedFirmwareVersion,
            status: filters?.status,
        },
    })

    const fwValidation = useMemo(() => firmwareInputValidation(productName, false), [productName])

    const submit = (formValues: FirmwareFormData) => {
        const chargePointsQuery: FirmwareGetChargePointsByFirmwareTypeApiArg = {
            id: formValues.id,
            partnerId: formValues.partnerId?.value,
            companyName: formValues.companyName,
            installedFirmwareVersion: formValues.firmwareVersion,
            status: filters?.status,
        }

        onSubmit(chargePointsQuery, pageClick)
    }

    const clearForm = () => {
        onClearFilters(),
            reset({
                firmwareVersion: '',
                id: '',
                companyName: '',
                partnerId: { value: undefined, label: 'Any partner' },
            })
    }

    return (
        <div>
            <STip data-testid="update-fw">
                <Text code="msgFWUpdateFWInfo" />
            </STip>

            <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-3">
                    <div className="form-group">
                        <Input
                            type="text"
                            labelText="Serial Number"
                            data-testid="input-serial-no"
                            id="serial-no"
                            {...register('id')}
                        />
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-3">
                    <div className="form-group">
                        <Input
                            type="text"
                            labelText="Organisation"
                            data-testid="input-org"
                            id="organization"
                            {...register('companyName')}
                        />
                    </div>
                </div>

                <div className="col-xs-12 col-sm-6 col-md-3">
                    <div className="form-group">
                        <Input
                            labelText="Firmware Version"
                            data-testid="input-fw-version"
                            id="fw-version"
                            error={formErrors.firmwareVersion?.message}
                            {...register('firmwareVersion', fwValidation)}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-3">
                    <div className="form-group">
                        <Select
                            isSearchable={false}
                            options={chargerStatuses}
                            defaultValue={chargerStatuses?.[0]}
                            value={chargerStatuses.find((option) => option.value === filters.status)}
                            onChange={(option) => onChange && onChange('status', option?.value)}
                            styles={{
                                control: (styles) => ({ ...styles, minHeight: '34px', fontSize: 14 }),
                                dropdownIndicator: (styles) => ({ ...styles, padding: '6px' }),
                            }}
                        />
                    </div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-3">
                    <div className="form-group" data-testid="input-partner">
                        <Controller
                            name="partnerId"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    isSearchable
                                    placeholder="Select partner"
                                    options={partnerOptions}
                                    defaultValue={partnerOptions?.[0]}
                                    styles={{
                                        control: (styles) => ({ ...styles, minHeight: '34px', fontSize: 14 }),
                                        dropdownIndicator: (styles) => ({ ...styles, padding: '6px' }),
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="col-xs-12 col-md-6">
                    <div className="form-inline">
                        <div className="pull-right">
                            <SButton
                                type="button"
                                className="btn btn-ok btn-sm heading5"
                                onClick={clearForm}
                                data-testid="btn-save"
                            >
                                <Text code="btnClear" />
                            </SButton>
                            <SButton
                                type="button"
                                className="btn btn-ok btn-sm heading5"
                                onClick={handleSubmit(submit)}
                                data-testid="btn-search"
                                disabled={!isValid}
                            >
                                <Text code="btnSearch" />
                                {isFetching && (
                                    <span
                                        className="fa fa-spinner fa-pulse margin-left-small"
                                        data-testid="btn-search"
                                    />
                                )}
                            </SButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UpdateChargersFirmwareFilters
